import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { useAuthStore } from './store/authStore';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import InvoicesPage from './pages/InvoicesPage';
import DebtorsPage from './pages/DebtorsPage';
import DebtorDetailPage from './pages/DebtorDetailPage';
import SettlementConfigPage from './pages/SettlementConfigPage';
import AdminCollectivitiesPage from './pages/AdminCollectivitiesPage';
import AdminMembersPage from './pages/AdminMembersPage';
import AdminMemberDetailPage from './pages/AdminMemberDetailPage';
import AdminClaimsPage from './pages/AdminClaimsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import NotificationsPage from './pages/NotificationsPage';
import AdminBoekstukkenPage from './pages/AdminBoekstukkenPage';
import AdminVerzekeringenPage from './pages/AdminVerzekeringenPage';
import AdminAllianzMatchingPage from './pages/AdminAllianzMatchingPage';
import AdminKredietchecksPage from './pages/AdminKredietchecksPage';
import AdminToegankelijkheidPage from './pages/AdminToegankelijkheidPage';
import AdminFinancieelPage from './pages/AdminFinancieelPage';
import AdminFinancieelInstellingenPage from './pages/AdminFinancieelInstellingenPage';
import AdminCollectiefConfigPage from './pages/AdminCollectiefConfigPage';
import AdminTemplatesPage from './pages/AdminTemplatesPage';
import AdminBerichtenPage from './pages/AdminBerichtenPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminLedenPage from './pages/AdminLedenPage';
import AdminKredietopiniesPage from './pages/AdminKredietopiniesPage';
import AdminToegangsverzoekenPage from './pages/AdminToegangsverzoekenPage';
import WachtwoordResetPage from './pages/WachtwoordResetPage';
import { ToastProvider } from './components/Toast';

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function App() {
  const { accessToken, fetchCurrentUser } = useAuthStore();

  // Restore user session on app load
  useEffect(() => {
    if (accessToken) {
      fetchCurrentUser(accessToken);
    }
  }, [accessToken, fetchCurrentUser]);

  return (
    <ToastProvider>
    <Router>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/wachtwoord-reset" element={<WachtwoordResetPage />} />
        <Route path="/register" element={<RegisterPage />} />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/invoices"
          element={
            <ProtectedRoute>
              <InvoicesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/debtors"
          element={
            <ProtectedRoute>
              <DebtorsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/debtors/:id"
          element={
            <ProtectedRoute>
              <DebtorDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settlement-config"
          element={
            <ProtectedRoute>
              <SettlementConfigPage />
            </ProtectedRoute>
          }
        />
        {/* "Afrekening" is samengevoegd in Verrekeningen (grootboeksaldi → Boekstukken, naverrekeningen → Verrekeningen) */}
        <Route path="/backoffice" element={<Navigate to="/backoffice/financieel" replace />} />
        <Route
          path="/backoffice/collectivities"
          element={
            <ProtectedRoute>
              <AdminCollectivitiesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/leden"
          element={
            <ProtectedRoute>
              <AdminLedenPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/toegangsverzoeken"
          element={
            <ProtectedRoute>
              <AdminToegangsverzoekenPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/collectivities/:id"
          element={
            <ProtectedRoute>
              <AdminMembersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/members/:id"
          element={
            <ProtectedRoute>
              <AdminMemberDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/kredietopinies"
          element={
            <ProtectedRoute>
              <AdminKredietopiniesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/kredietchecks"
          element={
            <ProtectedRoute>
              <AdminKredietchecksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/allianz-matching"
          element={
            <ProtectedRoute>
              <AdminAllianzMatchingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/verzekeringen"
          element={
            <ProtectedRoute>
              <AdminVerzekeringenPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/claims"
          element={
            <ProtectedRoute>
              <AdminClaimsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/incasso"
          element={
            <ProtectedRoute>
              <AdminClaimsPage vasteSoort="incasso" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/schade"
          element={
            <ProtectedRoute>
              <AdminClaimsPage vasteSoort="schade" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/dashboard"
          element={
            <ProtectedRoute>
              <AdminDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/berichten"
          element={
            <ProtectedRoute>
              <AdminBerichtenPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/toegankelijkheid"
          element={
            <ProtectedRoute>
              <AdminToegankelijkheidPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/financieel"
          element={
            <ProtectedRoute>
              <AdminFinancieelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/users"
          element={
            <ProtectedRoute>
              <AdminUsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/boekstukken"
          element={
            <ProtectedRoute>
              <AdminBoekstukkenPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/financieel-instellingen"
          element={
            <ProtectedRoute>
              <AdminFinancieelInstellingenPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/collectivities/:cid/config"
          element={
            <ProtectedRoute>
              <AdminCollectiefConfigPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backoffice/templates"
          element={
            <ProtectedRoute>
              <AdminTemplatesPage />
            </ProtectedRoute>
          }
        />

        {/* Placeholder routes for future pages */}
        <Route path="/credit-checks" element={<ProtectedRoute><div>Credit Checks (Coming Soon)</div></ProtectedRoute>} />
        <Route path="/claims" element={<ProtectedRoute><div>Claims (Coming Soon)</div></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><div>Settings (Coming Soon)</div></ProtectedRoute>} />

        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
    </ToastProvider>
  );
}

export default App;
